import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RackingArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { resolveRackingConnectNeighbors } from '@/modules/connections/factories/connection';

const OPTIONS: { labelTranslationKey: string; value: RackingArea['rackType'] }[] = [
  { labelTranslationKey: 'one_sided_inside', value: 'ONE_SIDED_INSIDE' },
  { labelTranslationKey: 'one_sided_alongside', value: 'ONE_SIDED_ALONGSIDE' },
  { labelTranslationKey: 'two_sided_alongside', value: 'TWO_SIDED_ALONGSIDE' },
];

const usesInsideConnectionDefaults = (rackType: RackingArea['rackType']): boolean =>
  rackType === 'ONE_SIDED_INSIDE';

const connectionDefaultsChange = (
  currentRackType: RackingArea['rackType'],
  nextRackType: RackingArea['rackType'],
): boolean =>
  usesInsideConnectionDefaults(currentRackType) !== usesInsideConnectionDefaults(nextRackType);

interface Props {
  selectedRackingAreas: RackingArea[];
}

/** @description Returns the rack with the new rack type and the defaults that come with it. */
const withRackType = (
  area: RackingArea,
  rackType: RackingArea['rackType'],
  connectNeighbors: RackingArea['connectNeighbors'],
): RackingArea => ({
  ...area,
  rackType,
  connectNeighbors,
  stationIdGenerator:
    rackType === 'TWO_SIDED_ALONGSIDE'
      ? areaFactory.buildTwoSidedRackingStationIdGenerator(area.name, area.rackSpec)
      : areaFactory.buildOneSidedRackingStationIdGenerator(area.name, area.rackSpec),
  alongsideExtension: areaFactory.buildRackAlongsideExtension(rackType),
  insideExtension: areaFactory.buildRackInsideExtension(rackType),
});

function RackingAreaTypeComponent({ selectedRackingAreas }: Props) {
  const { t } = useTranslation();
  const isConfirmingRef = useRef(false);
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  const rackType = useMemo(
    () => getCommonOption(selectedRackingAreas.map((area) => area.rackType)),
    [selectedRackingAreas],
  );

  const onChange = useCallback(
    async (newValue: RackingArea['rackType']) => {
      if (isConfirmingRef.current) return;

      const changedAreas = selectedRackingAreas.filter((area) => area.rackType !== newValue);
      if (changedAreas.length === 0) return;

      const connectNeighbors = resolveRackingConnectNeighbors(
        newValue,
        fddmSvc.getPreference('rackingArea.connectNeighbors'),
      );

      // Connections change when the connection category (inside/alongside) changes, but also when
      // connectNeighbors gets reset: in a mixed selection a rack can keep its category and still
      // get a different connectNeighbors (and so a different drive direction).
      const relatedConnections = useFloorplanDesignStore
        .getState()
        .interAreaConnectionActions.getRelatedTo(
          changedAreas
            .filter(
              (area) =>
                connectionDefaultsChange(area.rackType, newValue) ||
                area.connectNeighbors !== connectNeighbors,
            )
            .map(({ id }) => id),
        );

      const shouldAskAboutConnections = relatedConnections.length > 0;

      let shouldUpdateConnections = false;

      if (shouldAskAboutConnections) {
        isConfirmingRef.current = true;

        try {
          shouldUpdateConnections = Boolean(
            await confirm(
              t('interface:properties.area.rack.racking_type.confirm.title'),
              t('interface:properties.area.rack.racking_type.confirm.message'),
            ),
          );
        } finally {
          isConfirmingRef.current = false;
        }
      }

      const updatedAreas = createEmptyAreas();
      changedAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, withRackType(area, newValue, connectNeighbors));
      });

      const implicitConnections = relatedConnections.filter(
        (connection) => !connection.createdExplicitly,
      );

      historySvc.batch(() => {
        if (shouldAskAboutConnections && shouldUpdateConnections) {
          fddmSvc.deleteInterAreaConnections(relatedConnections);
        }

        if (
          shouldAskAboutConnections &&
          !shouldUpdateConnections &&
          implicitConnections.length > 0
        ) {
          /*
           * Mark these connections as explicit before updating the area.
           * This prevents updateArea from automatically switching their connection types.
           */
          fddmSvc.updateInterAreaConnections(implicitConnections);
        }

        fddmSvc.setPreference('rackingArea.rackType', newValue);
        fddmSvc.updateAreas(updatedAreas);
      });
    },
    [confirm, fddmSvc, historySvc, selectedRackingAreas, t],
  );

  const options = useMemo(() => {
    const labels = t('interface:properties.area.rack.racking_type.options', {
      returnObjects: true,
    });

    return OPTIONS.map((item) => ({
      label: labels[item.labelTranslationKey],
      value: item.value,
    }));
  }, [t]);

  return (
    <>
      {/* 'No' still changes the rack type, it only keeps the existing connections */}
      <Dialog anchor={ref} offset={[0, 100]} cancelLabel={t('common:no')} />
      <div ref={ref}>
        <SelectInput options={options} onChange={onChange} value={rackType} showMixed />
      </div>
    </>
  );
}

export const RackingAreaType = memo(RackingAreaTypeComponent);
